import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { GuardianInviteStateSchema } from '../guardian-invite-state/guardian-invite-state';
import { GuardianLabelSchema } from '../guardian-label/guardian-label';

/**
 * An invite the ward issued, as she reads it back.
 *
 * **The code is not here, and its absence is the design.** A code is a bearer
 * credential: whoever types it becomes a guardian. It is shown **once**, in the
 * response to the request that minted it ({@link GuardianInviteCreatedSchema}),
 * and after that only its SHA-256 exists — so a leaked database dump, a cached
 * list response, or a screenshot of this screen taken later contains nothing
 * anybody can redeem. It is the `refresh_tokens` discipline applied to the one
 * other place this platform hands out a secret.
 *
 * The consequence is deliberate and has to survive into the client: **there is
 * no "show me that code again"**. A ward who lost it cancels the invite and
 * issues another, which is also the only behaviour that leaves an honest trail
 * of how many keys to her conversations are outstanding.
 *
 * `state` is **derived** on every read, never stored — see
 * `resolveGuardianInviteState` for why a written `expired` is a lie with a
 * timestamp on it.
 */
export const GuardianInviteSchema = z
  .object({
    id: UuidSchema,
    /** What she calls this guardian. Hers, and never shown to him. */
    label: GuardianLabelSchema,
    state: GuardianInviteStateSchema,
    /** The first instant the code stops working. */
    expiresAt: z.iso.datetime(),
    /** When it was redeemed, or `null` while it has not been. */
    acceptedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
  })
  .strict();

export type GuardianInvite = z.infer<typeof GuardianInviteSchema>;
